import { Link } from 'react-router-dom'
import { ArrowRight, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { type TaskState } from '@/contexts/TaskManagerContext'
import { TaskProgressCard } from './TaskProgressCard'
import { TaskCompletedCard } from './TaskCompletedCard'

interface TaskPanelProps {
  /** Active/processing tasks */
  processing: TaskState[]
  /** Recently completed tasks */
  completed: TaskState[]
  /** Close the panel after navigation */
  onClose?: () => void
  /** Callback to dismiss a single task */
  onDismissTask?: (taskId: string) => void
  /** Callback to clear all completed tasks */
  onClearAll?: () => void
}

/**
 * Content panel for the task dropdown.
 * Shows processing tasks and recently completed tasks.
 */
export function TaskPanel({ processing, completed, onClose, onDismissTask, onClearAll }: TaskPanelProps) {
  const hasContent = processing.length > 0 || completed.length > 0

  return (
    <div className="space-y-4">
      {/* Empty state */}
      {!hasContent && (
        <div className="py-6 text-center text-sm text-muted-foreground">
          No active or recent tasks
        </div>
      )}

      {/* Processing tasks section */}
      {processing.length > 0 && (
        <section>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Processing ({processing.length})
          </h4>
          <div className="space-y-2">
            {processing.slice(0, 3).map((task) => (
              <TaskProgressCard key={task.taskId} task={task} compact />
            ))}
            {processing.length > 3 && (
              <p className="text-center text-xs text-muted-foreground">
                +{processing.length - 3} more
              </p>
            )}
          </div>
        </section>
      )}

      {/* Completed tasks section */}
      {completed.length > 0 && (
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Recently Completed
            </h4>
            {onClearAll && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAll}
                className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
              >
                <X className="mr-1 h-3 w-3" />
                Clear All
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {completed.slice(0, 3).map((task) => (
              <TaskCompletedCard
                key={task.taskId}
                task={task}
                compact
                {...(onDismissTask && { onDismiss: () => onDismissTask(task.taskId) })}
              />
            ))}
          </div>
        </section>
      )}

      {/* View all link */}
      <div className="border-t pt-3">
        <Link
          to="/tasks"
          onClick={onClose}
          className="flex items-center justify-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          View All Tasks
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  )
}
